import {
  Backdrop,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  TextField,
  Typography,
  useTheme,
  Chip,
  Divider,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import clock from "../../assets/clock.png";
import user from "../../assets/users.png";
import {
  cancelReservation,
  getReservationDetails,
  getWidgetSettings,
} from "../../api/HorecaWidget.service";
import { useDispatch, useSelector } from "react-redux";
import {
  setLanguageWords,
  setReservationDetail,
} from "../../store/slice/Addguest.slice";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import {
  updateCss,
  updateWidgetSetting,
} from "../../store/slice/widgetSetting.slice";
import { STATUSTYPEIDS } from "../../constants/moduleTypes";
import { format_Number } from "../../utils/utils";


const MONTH_MAP_KEY = [
  "", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const grandTotalGuests = (d) =>
  ["adults", "kid", "child", "toddler", "baby"].reduce(
    (s, k) => s + (Number(d?.[k]) || 0),
    0
  );

const InfoRow = ({ label, value, labelColor, valueColor }) => (
  <Box
    sx={{
      display: "flex",
      gap: 1,
      py: 0.6,
      borderBottom: "1px dashed rgba(0,0,0,0.06)",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Typography
      sx={{
        minWidth: 90,
        fontSize: "12px",
        fontWeight: 600,
        color: labelColor ?? "#9CA3AF",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontSize: "13px", color: valueColor ?? "#111827", fontWeight: 500 }}
    >
      {value}
    </Typography>
  </Box>
);

const SectionCard = ({ children, sx = {} }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: "18px",
      p: 2,
      height: "100%",
      background: "#fff",
      border: "1px solid rgba(0,0,0,0.07)",
      boxShadow: "0 4px 24px rgba(0,0,0,0.05)",
      transition: "box-shadow .2s",
      "&:hover": { boxShadow: "0 8px 32px rgba(0,0,0,0.09)" },
      ...sx,
    }}
  >
    {children}
  </Paper>
);
const CancelReservation = () => {
  const theme = useTheme();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const moduleId = queryParams.get("mainModuleId");
  const key = queryParams.get("key");
  const secretKey = queryParams.get("secretkey");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(false);
  const [isChecked, setIsChecked] = useState(true);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState(false);
  const INVALID_VALUES = ["null", "undefined", ""];
const isInvalid = (val) => !val || INVALID_VALUES.includes(String(val).trim());

  const { ReservationDetail } = useSelector((s) => s?.addGuest);
  const { WidgetSetting, css } = useSelector((s) => s?.widgetSettings);
  const { languageWords } = useSelector((s) => s?.addGuest);

  const Css = css?.find(
    (item) => item?.cssTabFieldsGroupName === "Success and cancel"
  )?.fields;

  /* ── date formatting ── */
  const DATE = dayjs(ReservationDetail?.arrivalDate).format("DD-MM-YYYY");
  const orig = dayjs(DATE, "DD-MM-YYYY");
  const monthLabel = languageWords?.[MONTH_MAP_KEY[orig.month() + 1]];
  const formattedDate = `${orig.date()} ${monthLabel ?? MONTH_MAP_KEY[orig.month() + 1]} ${orig.year()}`;
  const totalGuests = grandTotalGuests(ReservationDetail);

  /* ── helpers ── */
  const widgetVal = (tag) =>
    WidgetSetting?.find((i) => i?.tag === tag)?.data ?? "";

  const send = (...args) => {
    const [fH, fW, top, right, bottom, left, floating, redirectUrl] = args;
    window.parent.postMessage(
      {
        reservationIframeHeight: fH,
        reservationIframeWidth: fW,
        reservationIframePositionTop: top,
        reservationIframePositionRight: right,
        reservationIframePositionBottom: bottom,
        reservationIframePositionLeft: left,
        reservationIframePositionFloating: floating,
        redirectUrl,
      },
      "*"
    );
  };

  /* ── data fetching ── */
  const fetchReservation = async () => {
    setIsFetching(true);
    try {
      const response = await getReservationDetails(key, secretKey, moduleId);
      if (response?.status !== 200) {
        setError(true);
        return;
      }
      const result = response?.data?.basicLanguages?.reduce((acc, item) => {
        const tag = item.tag.replace(/{|}/g, "").replace(/\s+/g, "");
        acc[tag] = item.widgetTranslateLanguages[0]?.name;
        return acc;
      }, {});
      dispatch(updateCss(response?.data?.settings?.cssGroup));
      dispatch(setLanguageWords(result));
      dispatch(setReservationDetail(response?.data));
    } catch (e) {
      console.error("Error fetching reservation details:", e?.message);
    } finally {
      setIsFetching(false);
    }
  };

  const fetchWidgetSettings = async (language) => {
    try {
      const response = await getWidgetSettings(language, key);
      if (response?.status !== 200 || !response?.data) return;
      dispatch(updateWidgetSetting(response?.data));
    } catch (e) {
      console.error("Error fetching widget settings:", e?.message);
    }
  };

  const handleCancelReservation = async () => {
    if (!reason.trim()) {
      setReasonError(true);
      return;
    }
    setReasonError(false);
    setIsLoading(true);
    try {
      const response = await cancelReservation(key, secretKey, {
        key,
        secretKey,
        remarks: reason,
      });
      if (response?.status === 200) {
        navigate("/ReservationCancelSuccess");
      } else {
        alert("Error canceling reservation");
      }
    } catch (e) {
      console.error("Error canceling reservation:", e?.message);
    } finally {
      setIsLoading(false);
    }
  };

  /* ── effects ── */
  useEffect(() => {
    const parentUrl =
      document.referrer ||
      `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`;
    localStorage.setItem("parentUrl", parentUrl);
  }, []);

  useEffect(() => {
    document.body.setAttribute("style", "background-color: #F7F8FA !important");
    fetchReservation();
  }, []);

  useEffect(() => {
    if (ReservationDetail) {
      fetchWidgetSettings(ReservationDetail?.language?.slice(-2));
    }
  }, [ReservationDetail]);

  /* ── loading state ── */
  if (isFetching && !ReservationDetail) {
    return (
      <Backdrop
        sx={{ color: "#fff", zIndex: (t) => t.zIndex.drawer + 1 }}
        open={isFetching}
        onClick={() => setIsFetching(false)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    );
  }

  if (isInvalid(secretKey) || isInvalid(key) || isInvalid(moduleId)) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          px: 3,
          background: "#fafafa",
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: "50%",
            background: "linear-gradient(135deg,#f0f0f0,#e0e0e0)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography sx={{ fontSize: 28 }}>📋</Typography>
        </Box>
        <Typography sx={{ fontSize: { xs: "22px", sm: "28px" }, fontWeight: 600, color: "#1a1a1a" }}>
        
          {languageWords?.reservationNotFound ?? 'Reservation Not Found'}
        </Typography>
        <Typography sx={{ fontSize: { xs: "13px", sm: "15px" }, color: "#6b7280", maxWidth: 460, lineHeight: 1.7 }}>
         {languageWords?.reservationNotFoundInfo ?? "No reservation details are available. Kindly create a reservation first to access the cancellation page."} 
        </Typography>
      </Box>
    );
  }

  /* ── error state ── */
  if (error) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: "#F7F8FA",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 5,
            borderRadius: "20px",
            textAlign: "center",
            maxWidth: 420,
            border: "1px solid #FFE4E4",
            background: "#FFF8F8",
          }}
        >
          <Box sx={{ fontSize: "48px", mb: 2 }}>⚠️</Box>
          <Typography sx={{ fontWeight: 700, fontSize: "18px", color: "#DC2626", mb: 1 }}>
           {languageWords?.loadermessage ??  ' Unable to Load Reservation '}
          </Typography>
          <Typography sx={{ fontSize: "14px", color: "#6B7280" }}>
           {languageWords?.reservationFailMessage ?? 'Failed to fetch reservation details. Please try again or contact support.'} 
          </Typography>
        </Paper>
      </Box>
    );
  }



  /* ── not open state ── */
  if (ReservationDetail?.idstatus !== STATUSTYPEIDS?.OPEN) {
    send("100vh", "100%", "", "0px", "0px", "", "fixed");
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#F7F8FA",
          p: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: 460,
            width: "100%",
            p: 5,
            borderRadius: "24px",
            textAlign: "center",
            background: Css?.cancelBgColorCards ?? "#fff",
            border: "1px solid rgba(0,0,0,0.07)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.05)",
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              margin: "0 auto",
              borderRadius: "50%",
              background: "#FFF0F0",
              border: "1px solid #FECACA",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "32px",
              mb: 3,
            }}
          >
            🚫
          </Box>
          <Typography
            sx={{
              fontSize: "22px",
              fontWeight: 700,
              color: Css?.cancelHeadingColor ?? "#111827",
              mb: 1.5,
            }}
          >
           {languageWords?.NotAllowed ?? 'Cancellation Not Allowed'}
          </Typography>
          <Typography
            sx={{ fontSize: "14px", color: Css?.cancelInfoColor ?? "#6B7280", lineHeight: 1.7 }}
          >
           {languageWords.cancelationNotAllowedMessage ?? 'This reservation cannot be cancelled at this time. Please contact the service provider directly for further assistance.'} 
          </Typography>
        </Paper>
      </Box>
    );
  }

  /* ── main UI ── */
  send("100vh", "100%", "", "0px", "0px", "", "fixed");

  const canSubmit = isChecked && reason.trim();

  return (
    <Box
      sx={{
        maxWidth: 1100,
        mx: "auto",
        px: { xs: 1.5, sm: 2, md: 3 },
        py: { xs: 1.5, md: 2 },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ mb: 2 }}>
        {/* <Chip
          label="⚠️ Action Required"
          size="small"
          sx={{
            mb: 1,
            background: "#FFF3CD",
            color: "#92400E",
            fontWeight: 600,
            fontSize: "11px",
            letterSpacing: "0.05em",
            border: "1px solid #FDE68A",
          }}
        /> */}
        <Typography
          sx={{
            fontSize: { xs: "18px", md: "22px" },
            fontWeight: 800,
            color: Css?.cancelHeading ?? "#111827",
            lineHeight: 1.2,
            letterSpacing: "-0.02em",
          }}
        >
          {languageWords?.CancelReservation ?? "Cancel Reservation"}
        </Typography>
        <Typography
          sx={{
            mt: 0.5,
            fontSize: "13px",
            color: Css?.cancelInfo ?? "#6B7280",
            maxWidth: 560,
            lineHeight: 1.5,
          }}
        >
          {languageWords?.CancelInfo ??
            "Please review your booking details before proceeding with the cancellation."}
        </Typography>
      </Box>

      {/* ── Two-column cards ── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          mb: 2,
        }}
      >
        {/* Booking Card */}
        <SectionCard
          sx={{
            background: Css?.cancelYourBookingBgClr,
            borderColor: Css?.cancelYourBookingBorderClr,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "10px",
                background: "linear-gradient(135deg, #FFF0E0, #FFD9B0)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "16px",
              }}
            >
              🏨
            </Box>
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 700,
                color: Css?.cancelYourBookingHeadingClr ?? "#111827",
              }}
            >
              {languageWords?.yourBooking ?? "Your Booking"}
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: "17px",
              fontWeight: 700,
              color: Css?.cancelYourBookingAddressHeadingClr ?? "#111827",
              mb: 0.25,
            }}
          >
            {widgetVal("companyName")}
          </Typography>
          <Typography
            sx={{
              fontSize: "12px",
              color: Css?.cancelYourBookingAddressInfoClr ?? "#9CA3AF",
              mb: 1.5,
            }}
          >
            {widgetVal("companyAddress")}, {widgetVal("companyCity")}
          </Typography>

          <Divider sx={{ mb: 1.5, borderColor: "rgba(0,0,0,0.06)" }} />

          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: "10px",
                background: "rgba(0,0,0,0.03)",
                border: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <img src={clock} alt="time" width={14} />
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: Css?.cancelDateTimeClr ?? "#374151",
                }}
              >
                {formattedDate} · {ReservationDetail?.arrivalTime?.substring(0, 5)}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: "10px",
                background: "rgba(0,0,0,0.03)",
                border: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <img src={user} alt="guests" width={14} />
              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: Css?.cancelCountClr ?? "#374151",
                }}
              >
                {totalGuests} {totalGuests === 1 ? "Guest" : "Guests"}
              </Typography>
            </Box>

            {ReservationDetail?.paymentAmount > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: "10px",
                  background: "rgba(0,0,0,0.03)",
                  border: "1px solid rgba(0,0,0,0.06)",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "12px",
                    color: Css?.cancelDateTimeClr ?? "#374151",
                  }}
                >
                  {ReservationDetail?.reservationType?.name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: Css?.cancelCountClr ?? "#374151",
                  }}
                >
                  € {format_Number(ReservationDetail?.paymentAmount)}
                </Typography>
              </Box>
            )}
          </Box>
        </SectionCard>

        {/* Guest Card */}
        <SectionCard
          sx={{
            background: Css?.cancelGuestCardBgClr,
            borderColor: Css?.cancelBorderClr,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "10px",
                background: "linear-gradient(135deg, #EEF2FF, #C7D2FE)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "16px",
              }}
            >
              👤
            </Box>
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 700,
                color: Css?.cancelYourBookingHeadingClr ?? "#111827",
              }}
            >
              {languageWords?.yourDetails ?? "Your Details"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
            <InfoRow
              label={languageWords?.Name ?? "Name"}
              value={ReservationDetail?.name}
              labelColor={Css?.cancelLabelText}
              valueColor={Css?.cancelGuestTextClr}
            />
            <InfoRow
              label={languageWords?.Email ?? "Email"}
              value={ReservationDetail?.email}
              labelColor={Css?.cancelLabelText}
              valueColor={Css?.cancelGuestTextClr}
            />
            <InfoRow
              label={languageWords?.Phone ?? "Phone"}
              value={ReservationDetail?.phone}
              labelColor={Css?.cancelLabelText}
              valueColor={Css?.cancelGuestTextClr}
            />
          </Box>
        </SectionCard>
      </Box>

      {/* ── Reason textarea ── */}
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 700,
            color: Css?.cancelHeading ?? "#374151",
            mb: 0.75,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {languageWords?.ReasonforCancellation ?? "Reason for Cancellation"}
          <Typography component="span" sx={{ color: "#EF4444", ml: 0.5 }}>
            *
          </Typography>
        </Typography>

        <TextField
          multiline
          rows={3}
          fullWidth
          value={reason}
          placeholder={
            languageWords?.ReasonforCancellation ??
            "Please describe why you are cancelling this reservation…"
          }
          onChange={(e) => {
            setReason(e.target.value);
            if (e.target.value.trim()) setReasonError(false);
          }}
          error={reasonError}
          helperText={
            reasonError ? "A reason is required to proceed with the cancellation." : ""
          }
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "14px",
              fontSize: "14px",
              background: "#fff",
              "& fieldset": {
                borderColor: reasonError
                  ? "#EF4444"
                  : Css?.cancelYourBookingBorderClr ?? "rgba(0,0,0,0.12)",
                borderWidth: "1.5px",
              },
              "&:hover fieldset": {
                borderColor: Css?.cancelYourBookingBorderClr ?? "rgba(0,0,0,0.25)",
              },
              "&.Mui-focused fieldset": {
                borderColor: Css?.CancelReservationBgClr ?? "#6366F1",
                borderWidth: "2px",
              },
            },
            "& .MuiFormHelperText-root": {
              fontSize: "12px",
              mt: 0.5,
              color: "#EF4444",
            },
          }}
        />
      </Box>

      {/* ── Footer: checkbox + button ── */}
      <Box
        sx={{
          p: 1.5,
          borderRadius: "16px",
          background: "#fff",
          border: "1px solid rgba(0,0,0,0.07)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              checked={isChecked}
              onChange={() => setIsChecked((v) => !v)}
              size="small"
              sx={{
                color: "rgba(0,0,0,0.2)",
                "&.Mui-checked": {
                  color: Css?.cancelCheckboxTickColor ?? "#6366F1",
                },
              }}
            />
          }
          label={
            <Typography
              sx={{ fontSize: "12px", color: Css?.cancelLabelCheckBoxClr ?? "#374151" }}
            >
              {languageWords?.TermsandCondtionsText ??
                "I agree to the terms and conditions of this cancellation."}
            </Typography>
          }
        />

        <Button
          disabled={!canSubmit}
          onClick={handleCancelReservation}
          sx={{
            px: 3,
            py: 1,
            borderRadius: "12px",
            minWidth: 160,
            fontWeight: 700,
            fontSize: "13px",
            textTransform: "none",
            letterSpacing: "0.01em",
            background: canSubmit
              ? Css?.CancelReservationBgClr ?? "#DC2626"
              : Css?.CancelReservationDisabledBgClr ?? "#F3F4F6",
            color: canSubmit
              ? Css?.cancelReservationTxtClr ?? "#fff"
              : Css?.CancelReservationDisabledTxtClr ?? "#9CA3AF",
            boxShadow: canSubmit ? "0 4px 14px rgba(220,38,38,0.3)" : "none",
            transition: "all 0.2s ease",
            "&:hover": {
              background: canSubmit
                ? Css?.CancelReservationBgClr ?? "#B91C1C"
                : Css?.CancelReservationDisabledBgClr ?? "#F3F4F6",
              boxShadow: canSubmit ? "0 6px 20px rgba(220,38,38,0.4)" : "none",
              transform: canSubmit ? "translateY(-1px)" : "none",
            },
            "&:disabled": {
              background: Css?.CancelReservationDisabledBgClr ?? "#F3F4F6",
              color: Css?.CancelReservationDisabledTxtClr ?? "#9CA3AF",
            },
          }}
        >
          {isLoading ? (
            <CircularProgress size={18} sx={{ color: "#fff" }} />
          ) : (
            languageWords?.CancelWidgetButtonText ?? "Cancel Reservation"
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default CancelReservation;
